import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import { triggerDefs } from "./types";

// ── Types ─────────────────────────────────────────────────────────────────────

interface RoutingHistoryItem {
  id: number;
  conversationId: number | null;
  occurredAt: string;
  trigger: string;
  ruleId: number | null;
  ruleName: string | null;
  workflowId: number | null;
  workflowName: string | null;
  outcomeSource: string;
  outcomeType: string;
  outcomeId: number | null;
  outcomeLabel: string | null;
  conditionsEvaluated: number;
  failedRules?: FailedRuleEval[];
}

interface FailedRuleEval {
  ruleId: number;
  ruleName: string;
  reason: string;
}

interface RoutingHistoryPage {
  total: number;
  page: number;
  pageSize: number;
  items: RoutingHistoryItem[];
}

interface GroupedEvent {
  trigger: string;
  ruleId: number | null;
  ruleName: string | null;
  workflowId: number | null;
  workflowName: string | null;
  outcomeSource: string;
  outcomeType: string;
  outcomeLabel: string | null;
  count: number;
  lastOccurredAt: string;
  conversationIds: (number | null)[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<string, string> = triggerDefs as Record<string, string>;

const OUTCOME_TYPE_LABELS: Record<string, string> = {
  survey_shown: "Pre-Chat Survey",
  routed_to_team: "Routed to Team",
  workflow_started: "Workflow Started",
  unavailable_survey_shown: "Unavailable Survey",
  postchat_survey_shown: "Post-Chat Survey",
  bot_assigned: "AI Bot",
  ai_started: "AI Bot",
  priority_set: "Priority Set",
  chat_window_set: "Chat Window Set",
  crm_record_created: "CRM Record Created",
  resolved: "Auto-Resolved",
  no_match: "No Match — Failed"
};

const OUTCOME_COLORS: Record<string, string> = {
  survey_shown: "bg-purple-100 text-purple-800",
  routed_to_team: "bg-blue-100 text-blue-800",
  workflow_started: "bg-emerald-100 text-emerald-800",
  unavailable_survey_shown: "bg-amber-100 text-amber-800",
  postchat_survey_shown: "bg-pink-100 text-pink-800",
  bot_assigned: "bg-violet-100 text-violet-800",
  ai_started: "bg-violet-100 text-violet-800",
  priority_set: "bg-orange-100 text-orange-800",
  chat_window_set: "bg-slate-100 text-slate-700",
  crm_record_created: "bg-teal-100 text-teal-800",
  resolved: "bg-gray-100 text-gray-700",
  no_match: "bg-red-100 text-red-700"
};

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  rule_match: { label: "Rule", color: "bg-cornflower-blue-100 text-cornflower-blue-800" },
  deployment_default: { label: "Default", color: "bg-slate-100 text-slate-600" },
  no_match: { label: "No Match", color: "bg-red-50 text-red-600" }
};

const DAY_OPTIONS = [
  { label: "Last 24 hours", value: 1 },
  { label: "Last 7 days", value: 7 },
  { label: "Last 14 days", value: 14 },
  { label: "Last 30 days", value: 30 }
];

const PAGE_SIZE = 50;

function formatTime(iso: string): string {
  try {
    // Backend stores UTC but may omit the Z suffix — append it so the browser
    // interprets the value as UTC and converts to the user's local timezone.
    const normalized = /Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z";
    const d = new Date(normalized);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short"
    });
  } catch {
    return iso;
  }
}

function groupEvents(items: RoutingHistoryItem[]): GroupedEvent[] {
  const map = new Map<string, GroupedEvent>();
  for (const item of items) {
    const key = `${item.trigger}|${item.ruleId ?? ""}|${item.outcomeType}|${item.outcomeLabel ?? ""}`;
    const existing = map.get(key);
    if (existing) {
      existing.count++;
      existing.lastOccurredAt = item.occurredAt > existing.lastOccurredAt ? item.occurredAt : existing.lastOccurredAt;
      if (existing.conversationIds.length < 5) {
        existing.conversationIds.push(item.conversationId);
      }
    } else {
      map.set(key, {
        trigger: item.trigger,
        ruleId: item.ruleId,
        ruleName: item.ruleName,
        workflowId: item.workflowId,
        workflowName: item.workflowName,
        outcomeSource: item.outcomeSource,
        outcomeType: item.outcomeType,
        outcomeLabel: item.outcomeLabel,
        count: 1,
        lastOccurredAt: item.occurredAt,
        conversationIds: [item.conversationId]
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

// ── Badge components ───────────────────────────────────────────────────────────

function OutcomeBadge({ type }: { type: string }) {
  const color = OUTCOME_COLORS[type] ?? "bg-slate-100 text-slate-500";
  const label = OUTCOME_TYPE_LABELS[type] ?? type;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const def = SOURCE_LABELS[source] ?? { label: source, color: "bg-slate-100 text-slate-500" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${def.color}`}>
      {def.label}
    </span>
  );
}

function TriggerLabel({ trigger }: { trigger: string }) {
  const label = TRIGGER_LABELS[trigger] ?? trigger;
  return (
    <span className="text-sm text-slate-700 font-medium" title={trigger}>
      {label}
    </span>
  );
}

// ── Stats Strip ───────────────────────────────────────────────────────────────

function StatsStrip({ items, total }: { items: RoutingHistoryItem[]; total: number }) {
  const ruleMatches = items.filter((i) => i.outcomeSource === "rule_match").length;
  const noMatches = items.filter((i) => i.outcomeType === "no_match").length;

  if (total === 0) return null;

  return (
    <div className="flex items-center gap-6 py-2 px-1 text-sm text-slate-500 border-b border-slate-100">
      <span>
        <strong className="text-slate-800">{total}</strong> event{total !== 1 ? "s" : ""} found
      </span>
      <span>
        <strong className="text-cornflower-blue-700">{ruleMatches}</strong> rule matches
        {ruleMatches < total && " (on this page)"}
      </span>
      {noMatches > 0 && (
        <span>
          <strong className="text-red-600">{noMatches}</strong> failed (no match)
          {noMatches < total && " (on this page)"}
        </span>
      )}
    </div>
  );
}

// ── Retrigger button ───────────────────────────────────────────────────────────

function RetriggerButton({ conversationId, api }: { conversationId: number; api: ReturnType<typeof useApi> }) {
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [result, setResult] = useState<string | null>(null);

  const retrigger = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setStatus("loading");
    try {
      const res = await api.messaging.post(`RoutingHistory/conversations/${conversationId}/retrigger`, {});
      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        setResult(json?.outcomeLabel ?? json?.outcomeType ?? "Retriggered");
        setStatus("done");
      } else {
        setStatus("error");
        setResult("Not supported yet");
      }
    } catch {
      setStatus("error");
      setResult("Error");
    }
    setTimeout(() => { setStatus("idle"); setResult(null); }, 4000);
  };

  if (status === "done") return <span className="text-xs text-emerald-600 font-medium">✓ {result}</span>;
  if (status === "error") return <span className="text-xs text-red-500">{result}</span>;

  return (
    <button
      onClick={retrigger}
      disabled={status === "loading"}
      className="text-xs text-cornflower-blue-600 hover:text-cornflower-blue-800 border border-cornflower-blue-200 hover:border-cornflower-blue-400 rounded px-2 py-0.5 transition-colors disabled:opacity-40"
      title="Re-evaluate routing rules for this conversation"
      aria-label="Retrigger routing evaluation"
    >
      {status === "loading" ? "…" : "↺ Retrigger"}
    </button>
  );
}

// ── Help panel ────────────────────────────────────────────────────────────────

function HelpPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
        aria-expanded={open}
        aria-label="Toggle routing history help"
      >
        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-slate-300 text-slate-400 text-[10px] font-bold leading-none">?</span>
        {open ? "Hide help" : "What is this? What does Retrigger do?"}
      </button>
      {open && (
        <div className="mt-2 p-4 bg-cornflower-blue-50 border border-cornflower-blue-100 rounded-xl text-sm text-slate-700 space-y-3 max-w-3xl">
          <div>
            <p className="font-semibold text-slate-800 mb-1">Routing History</p>
            <p>Every routing decision Velaro makes is logged here — which rule fired, what matched, and where it failed. Use this to understand why a conversation was sent to a specific team, bot, or survey, or to investigate a routing failure.</p>
          </div>
          <div>
            <p className="font-semibold text-slate-800 mb-1">↺ Retrigger — what it does</p>
            <p>The <strong>Retrigger</strong> button re-runs the routing engine against a past conversation in <strong>read-only simulation mode</strong>. It tells you exactly what would happen if that conversation came in right now under your current rules — without actually doing anything. No agents are notified, no workflows start, no data is changed. It is 100% safe to use at any time, on any conversation.</p>
            <p className="mt-1 text-slate-500 text-xs">Use it to verify that a rule fix is working before the next live chat arrives.</p>
          </div>
          <div>
            <p className="font-semibold text-slate-800 mb-1">Tips</p>
            <ul className="list-disc pl-4 space-y-1 text-slate-600">
              <li>Switch to <strong>Grouped</strong> view on high-volume sites to spot patterns without scrolling thousands of rows.</li>
              <li>Filter by <strong>Failed only</strong> to focus on conversations that didn't route correctly.</li>
              <li>Click any rule or workflow name to navigate directly to it for editing.</li>
              <li>Ask the <strong>Copilot</strong> (? button) to diagnose a specific conversation: "Why wasn't conversation 12345 routed?" and it will run a simulation and explain the result.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function RoutingHistory() {
  const api = useApi();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [data, setData] = useState<RoutingHistoryPage | null>(null);
  const [availableTriggers, setAvailableTriggers] = useState<string[]>([]);

  // View mode
  const [grouped, setGrouped] = useState(false);

  // Filters
  const [days, setDays] = useState(7);
  const [triggerFilter, setTriggerFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [ruleSearch, setRuleSearch] = useState("");
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const params = new URLSearchParams({
        days: String(days),
        page: String(page),
        pageSize: String(PAGE_SIZE)
      });
      if (triggerFilter) params.set("trigger", triggerFilter);
      if (outcomeFilter) params.set("outcomeType", outcomeFilter);
      if (showFailedOnly) params.set("outcomeType", "no_match");

      const response = await api.messaging.get(`RoutingHistory?${params}`);
      if (!response.ok) {
        setApiError(`API returned ${response.status} — the routing history endpoint may not be enabled yet.`);
        setData({ total: 0, page: 1, pageSize: PAGE_SIZE, items: [] });
        return;
      }
      const json = await response.json();
      setData({
        total: json?.total ?? 0,
        page: json?.page ?? 1,
        pageSize: json?.pageSize ?? PAGE_SIZE,
        items: Array.isArray(json?.items) ? json.items : []
      });
    } catch (err) {
      setApiError("Could not reach the routing history API. Check network or backend status.");
      setData({ total: 0, page: 1, pageSize: PAGE_SIZE, items: [] });
    } finally {
      setLoading(false);
    }
  }, [api, days, triggerFilter, outcomeFilter, showFailedOnly, page]);

  // Fetch available triggers for dropdown (once, on mount)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.messaging.get("RoutingHistory/Triggers?days=30");
        if (res.ok) {
          const json = await res.json();
          setAvailableTriggers(Array.isArray(json) ? json : []);
        }
      } catch {
        setAvailableTriggers([]);
      }
    })();
  }, [api]);

  useEffect(() => {
    setPage(1);
  }, [days, triggerFilter, outcomeFilter, showFailedOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Client-side rule name filter
  const baseItems = data?.items ?? [];
  const visibleItems = ruleSearch.trim()
    ? baseItems.filter((i) =>
        (i.ruleName ?? "").toLowerCase().includes(ruleSearch.toLowerCase()) ||
        (i.workflowName ?? "").toLowerCase().includes(ruleSearch.toLowerCase())
      )
    : baseItems;

  const groupedItems = grouped ? groupEvents(visibleItems) : [];

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const goToRule = (ruleId: number) => navigate(`/rules`);
  const goToWorkflow = (workflowId: number) => navigate(`/workflows/${workflowId}`);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Routing History</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Every routing decision — which rule fired, what matched, and where it failed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {DAY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={fetchData}
            className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm text-slate-600 hover:bg-slate-50 transition-colors"
            title="Refresh"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Help panel */}
      <HelpPanel />

      {/* API error banner */}
      {apiError && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <strong>Could not load routing history:</strong> {apiError}
          <p className="mt-1 text-red-600 text-xs">
            Make sure routing history logging is enabled in the backend and the RoutingHistoryController is deployed.
          </p>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300 min-w-[180px]"
          value={triggerFilter}
          onChange={(e) => setTriggerFilter(e.target.value)}
        >
          <option value="">All trigger types</option>
          {availableTriggers.map((t) => (
            <option key={t} value={t}>{TRIGGER_LABELS[t] ?? t}</option>
          ))}
        </select>

        <select
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300 min-w-[160px]"
          value={outcomeFilter}
          onChange={(e) => setOutcomeFilter(e.target.value)}
        >
          <option value="">All outcomes</option>
          {Object.entries(OUTCOME_TYPE_LABELS)
            .filter(([k]) => k !== "ai_started") // deduplicate AI bot alias
            .map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
        </select>

        <input
          type="text"
          placeholder="Filter by rule or workflow name…"
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300 min-w-[200px] flex-1"
          value={ruleSearch}
          onChange={(e) => setRuleSearch(e.target.value)}
        />

        {/* Failed-only toggle */}
        <button
          onClick={() => setShowFailedOnly((v) => !v)}
          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            showFailedOnly
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
          }`}
        >
          {showFailedOnly ? "✕ Show failed only" : "⚠ Failed only"}
        </button>

        {/* Group/Raw toggle */}
        <button
          onClick={() => setGrouped((v) => !v)}
          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            grouped
              ? "bg-cornflower-blue-50 border-cornflower-blue-200 text-cornflower-blue-700"
              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
          }`}
          title="Consolidate repeated events with a count — useful for high-volume sites"
        >
          {grouped ? "📊 Grouped" : "📋 Raw"}
        </button>

        {(triggerFilter || outcomeFilter || ruleSearch || showFailedOnly) && (
          <button
            className="text-sm text-slate-400 hover:text-slate-600 px-2"
            onClick={() => {
              setTriggerFilter("");
              setOutcomeFilter("");
              setRuleSearch("");
              setShowFailedOnly(false);
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Stats strip */}
      {!loading && data && !grouped && (
        <StatsStrip items={data.items} total={data.total} />
      )}

      {/* Table */}
      <div className="mt-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : grouped ? (
          <GroupedTable
            groups={groupedItems}
            onGoToRule={goToRule}
            onGoToWorkflow={goToWorkflow}
          />
        ) : visibleItems.length === 0 ? (
          <EmptyState
            hasFilters={!!(triggerFilter || outcomeFilter || ruleSearch || showFailedOnly)}
            days={days}
            hasApiError={!!apiError}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Time</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Conversation</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Trigger</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Rule / Workflow</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Source</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Outcome</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Result</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleItems.map((item) => (
                  <RoutingHistoryRow
                    key={item.id}
                    item={item}
                    api={api}
                    onGoToRule={goToRule}
                    onGoToWorkflow={goToWorkflow}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !grouped && data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
          <span>
            Showing {(data.page - 1) * PAGE_SIZE + 1}–{Math.min(data.page * PAGE_SIZE, data.total)} of{" "}
            {data.total}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className="px-2">Page {page} of {totalPages}</span>
            <button
              className="px-3 py-1.5 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-3 text-xs text-slate-400">
        <span className="font-medium text-slate-500">Outcomes:</span>
        {Object.entries(OUTCOME_TYPE_LABELS).map(([k, v]) => (
          <span key={k} className={`px-2 py-0.5 rounded-full font-medium ${OUTCOME_COLORS[k] ?? "bg-slate-100 text-slate-500"}`}>
            {v}
          </span>
        ))}
      </div>

      {/* High-volume note */}
      {data && data.total > 500 && (
        <div className="mt-3 text-xs text-slate-400">
          Tip: Switch to <strong>Grouped</strong> view to consolidate repeated events and spot patterns without scrolling thousands of rows.
        </div>
      )}
    </div>
  );
}

// ── Row component ─────────────────────────────────────────────────────────────

function RoutingHistoryRow({
  item,
  api,
  onGoToRule,
  onGoToWorkflow
}: {
  item: RoutingHistoryItem;
  api: ReturnType<typeof useApi>;
  onGoToRule(id: number): void;
  onGoToWorkflow(id: number): void;
}) {
  const isFailed = item.outcomeType === "no_match";

  return (
    <tr className={`hover:bg-slate-50 transition-colors ${isFailed ? "bg-red-50/30" : ""}`}>
      {/* Time */}
      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
        {formatTime(item.occurredAt)}
      </td>

      {/* Conversation */}
      <td className="px-4 py-3">
        {item.conversationId ? (
          <span className="font-mono text-xs text-cornflower-blue-700 bg-cornflower-blue-50 px-1.5 py-0.5 rounded">
            #{item.conversationId}
          </span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>

      {/* Trigger */}
      <td className="px-4 py-3">
        <TriggerLabel trigger={item.trigger} />
      </td>

      {/* Rule / Workflow — clickable */}
      <td className="px-4 py-3">
        {item.ruleName && item.ruleId ? (
          <button
            onClick={() => onGoToRule(item.ruleId!)}
            className="text-sm text-cornflower-blue-700 hover:text-cornflower-blue-900 hover:underline text-left"
            title="Click to open Rules page"
          >
            {item.ruleName}
            <span className="ml-1 text-xs text-slate-400">#{item.ruleId}</span>
          </button>
        ) : item.workflowName && item.workflowId ? (
          <button
            onClick={() => onGoToWorkflow(item.workflowId!)}
            className="text-sm text-emerald-700 hover:text-emerald-900 hover:underline text-left"
            title="Click to open workflow"
          >
            {item.workflowName}
          </button>
        ) : isFailed ? (
          <span className="text-xs text-red-500 font-medium">No rule matched</span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>

      {/* Source */}
      <td className="px-4 py-3">
        <SourceBadge source={item.outcomeSource} />
      </td>

      {/* Outcome type */}
      <td className="px-4 py-3">
        <OutcomeBadge type={item.outcomeType} />
      </td>

      {/* Outcome label */}
      <td className="px-4 py-3">
        {item.outcomeLabel ? (
          <span className="text-sm text-slate-600">{item.outcomeLabel}</span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        {item.conversationId ? (
          <RetriggerButton conversationId={item.conversationId} api={api} />
        ) : (
          <span className="text-slate-200 text-xs">—</span>
        )}
      </td>
    </tr>
  );
}

// ── Grouped table ─────────────────────────────────────────────────────────────

function GroupedTable({
  groups,
  onGoToRule,
  onGoToWorkflow
}: {
  groups: GroupedEvent[];
  onGoToRule(id: number): void;
  onGoToWorkflow(id: number): void;
}) {
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-slate-500 font-medium">No events to group</p>
        <p className="text-slate-400 text-sm mt-1">Try widening the date range or clearing filters</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
          <tr>
            <th className="px-4 py-3 font-semibold">Count</th>
            <th className="px-4 py-3 font-semibold">Last seen</th>
            <th className="px-4 py-3 font-semibold">Trigger</th>
            <th className="px-4 py-3 font-semibold">Rule / Workflow</th>
            <th className="px-4 py-3 font-semibold">Outcome</th>
            <th className="px-4 py-3 font-semibold">Result</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {groups.map((g, i) => {
            const isFailed = g.outcomeType === "no_match";
            return (
              <tr key={i} className={`hover:bg-slate-50 transition-colors ${isFailed ? "bg-red-50/30" : ""}`}>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center justify-center w-10 h-7 rounded-full text-xs font-bold ${
                    isFailed ? "bg-red-100 text-red-700" : "bg-cornflower-blue-100 text-cornflower-blue-800"
                  }`}>
                    {g.count}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                  {formatTime(g.lastOccurredAt)}
                </td>
                <td className="px-4 py-3">
                  <TriggerLabel trigger={g.trigger} />
                </td>
                <td className="px-4 py-3">
                  {g.ruleName && g.ruleId ? (
                    <button
                      onClick={() => onGoToRule(g.ruleId!)}
                      className="text-sm text-cornflower-blue-700 hover:underline text-left"
                    >
                      {g.ruleName}
                    </button>
                  ) : g.workflowName && g.workflowId ? (
                    <button
                      onClick={() => onGoToWorkflow(g.workflowId!)}
                      className="text-sm text-emerald-700 hover:underline text-left"
                    >
                      {g.workflowName}
                    </button>
                  ) : isFailed ? (
                    <span className="text-xs text-red-500 font-medium">No rule matched</span>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <OutcomeBadge type={g.outcomeType} />
                </td>
                <td className="px-4 py-3">
                  {g.outcomeLabel ? (
                    <span className="text-sm text-slate-600">{g.outcomeLabel}</span>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────

function EmptyState({ hasFilters, days, hasApiError }: { hasFilters: boolean; days: number; hasApiError: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center px-8">
      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>

      {hasApiError ? (
        <>
          <p className="text-slate-600 font-medium">Routing history API is not responding</p>
          <p className="text-slate-400 text-sm mt-1 max-w-md">
            The backend <code className="text-xs bg-slate-100 px-1 rounded">RoutingHistoryController</code> needs to be
            deployed before events will appear here. Once live, every routing decision will be logged automatically.
          </p>
        </>
      ) : hasFilters ? (
        <>
          <p className="text-slate-600 font-medium">No routing events match your filters</p>
          <p className="text-slate-400 text-sm mt-1">Try widening the date range or clearing filters</p>
        </>
      ) : (
        <>
          <p className="text-slate-600 font-medium">No routing events in the last {days} day{days !== 1 ? "s" : ""}</p>
          <p className="text-slate-400 text-sm mt-2 max-w-md">
            Routing decisions are logged whenever rules evaluate a conversation. If chats are coming in but nothing
            appears here, the routing history logging may not yet be active on the backend.
          </p>
          <p className="text-slate-400 text-sm mt-1 max-w-md">
            Try the <strong>Last 30 days</strong> date range, or check whether the
            <code className="text-xs bg-slate-100 px-1 mx-1 rounded">RoutingHistory</code>
            table exists in the messaging database.
          </p>
        </>
      )}
    </div>
  );
}
